import { useMemo } from 'react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useWorkspaceDetails } from '@/features/workspace/hooks/useWorkspaceDetails'
import { useSelectionStore } from '@/state/selection.store'
import { safeLocalStorage } from '@/lib/safe-storage'
import { getPendingUpgrade } from '@/features/sitemap/lib/pendingUpgrade'

export const makeGetAuthPayload = ({ getUser, getWorkspace, getProjectId, getCheckoutContext }) => () => {
  const user = getUser()
  const workspace = getWorkspace()
  // Optional checkout attribution (e.g. Website Canvas: { source, feature }).
  // The pricing iframe forwards source/feature into the Stripe session
  // metadata, which the webhook uses to finish flows server-side.
  const checkoutContext = (typeof getCheckoutContext === 'function' ? getCheckoutContext() : null) || {}
  const activePlans = Array.isArray(workspace?.activePlans) ? workspace.activePlans : []
  const allPriceIds = activePlans.flatMap((p) => Array.isArray(p?.stripe?.priceIds) ? p.stripe.priceIds : p?.stripe?.priceId ? [p.stripe.priceId] : [])
  const uniquePriceIds = [...new Set(allPriceIds)].filter(Boolean)
  const currentPlanPriceId = uniquePriceIds.length === 0 ? null : uniquePriceIds.length === 1 ? uniquePriceIds[0] : uniquePriceIds

  return {
    token: user?.token || safeLocalStorage.getItem('auth_token') || null,
    userId: user?.id || user?._id || safeLocalStorage.getItem('uRI') || null,
    workspaceId: workspace?.id || workspace?._id || null,
    hideCredits: Boolean(workspace?.hideCredits),
    workspace: workspace || {},
    currentPlanPriceId,
    returnUrl: window.location.href,
    cancelUrl: window.location.href,
    // Reliable project attribution: the active project id (null at workspace level).
    projectId: (typeof getProjectId === 'function' ? getProjectId() : null) || null,
    ...(checkoutContext.source ? { source: checkoutContext.source } : {}),
    ...(checkoutContext.feature ? { feature: checkoutContext.feature } : {}),
  }
}

export function usePricingAuth() {
  const { user } = useAuth()
  const { workspace } = useWorkspaceDetails()
  const activeProjectId = useSelectionStore((s) => s.activeProjectId)

  const getAuthPayload = useMemo(
    () => makeGetAuthPayload({
      getUser: () => user,
      getWorkspace: () => workspace,
      getProjectId: () => activeProjectId,
      // Website Canvas: when the canvas initiated this pricing flow (a
      // pendingUpgrade marker exists), tag the checkout so the Stripe
      // webhook can complete the license+attach upgrade server-side even
      // if the user never returns to the tab.
      getCheckoutContext: () => {
        const pending = getPendingUpgrade()
        if (!pending) return null
        return { source: 'website-canvas', feature: pending.intent || 'continue' }
      },
    }),
    [user, workspace, activeProjectId]
  )

  return { getAuthPayload, workspace }
}
